import SectionHeading from "@/components/base/SectionHeading";
import VerdictBadge from "@/components/base/VerdictBadge";
import { featuredCases } from "@/mocks/home";

type CaseType = (typeof featuredCases)[number];

function LargeCase({ item }: { item: CaseType }) {
  return (
    <a
      href="#top"
      className="group relative block w-full h-[380px] md:h-[460px] rounded-lg overflow-hidden border border-background-200 cursor-pointer"
    >
      <img
        src={item.cover}
        alt={`${item.title} 黑料照片鉴定案例封面`}
        title={`${item.title} - 黑料照片P图鉴定`}
        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/25 to-transparent"></div>
      <div className="absolute top-5 left-5 right-5 flex items-start justify-between gap-3">
        <span className="font-label text-xs px-3 py-1.5 rounded-full bg-background-50/90 text-foreground-800 backdrop-blur">
          {item.tag}
        </span>
        <VerdictBadge verdict={item.verdict} type={item.verdictType} size="md" />
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7">
        <div className="flex items-center gap-2 mb-3">
          <span className="font-label text-[11px] px-2 py-0.5 rounded bg-accent-500 text-foreground-950">
            本期深度拆解
          </span>
        </div>
        <h3 className="font-heading font-black text-xl md:text-3xl text-background-50">
          {item.title}
        </h3>
        <p className="mt-3 text-sm text-secondary-100 max-w-2xl leading-relaxed">
          {item.summary}
        </p>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 text-xs text-secondary-200">
          <span className="flex items-center gap-1.5">
            <i className="ri-eye-line"></i> {item.views} 浏览
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-chat-3-line"></i> {item.discuss} 讨论
          </span>
          <span className="flex items-center gap-1.5 text-accent-300">
            查看完整拆解报告 <i className="ri-arrow-right-line"></i>
          </span>
        </div>
      </div>
    </a>
  );
}

function SmallCase({ item }: { item: CaseType }) {
  return (
    <a
      href="#top"
      className="group flex flex-col rounded-lg overflow-hidden border border-background-200 bg-background-50 cursor-pointer hover:border-primary-300 transition-colors"
    >
      <div className="relative w-full h-40 md:h-44 overflow-hidden">
        <img
          src={item.cover}
          alt={`${item.title} 黑料照片真假分析封面`}
          title={`${item.title} - 黑料照片真假分析`}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <span className="absolute top-3 left-3 font-label text-[11px] px-2 py-1 rounded bg-background-50/90 text-foreground-800 backdrop-blur">
          {item.tag}
        </span>
      </div>
      <div className="flex-1 flex flex-col p-4 md:p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-heading font-bold text-sm md:text-base text-foreground-950 leading-snug line-clamp-2">
            {item.title}
          </h3>
          <VerdictBadge verdict={item.verdict} type={item.verdictType} />
        </div>
        <p className="mt-2 text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2">
          {item.summary}
        </p>
        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-background-100 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i> {item.views}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-chat-3-line"></i> {item.discuss}
          </span>
        </div>
      </div>
    </a>
  );
}

export default function CaseGallery() {
  const [lead, ...rest] = featuredCases;
  const sideColumn = rest.slice(0, 2);
  const bottomRow = rest.slice(2);

  return (
    <section id="cases" className="relative w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
      <SectionHeading
        eyebrow="CASE FILES"
        title="精选鉴定案例"
        desc="每张争议照片都附完整拆解：先列疑点，再上证据，最后给出「实锤 / 伪造 / 存疑」结论。"
        actionLabel="浏览全部案例"
        actionHref="#top"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <LargeCase item={lead} />
        </div>
        <div className="flex flex-col gap-5">
          {sideColumn.map((item) => (
            <SmallCase key={item.id} item={item} />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
        {bottomRow.map((item) => (
          <SmallCase key={item.id} item={item} />
        ))}
      </div>
    </section>
  );
}